import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, CircularProgress, Tab, Tabs } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../services/api';

const COLORS = ['#1976d2','#2e7d32','#ed6c02','#9c27b0','#0288d1','#c62828'];

export const AnalyticsPage: React.FC = () => {
  const [revenue, setRevenue] = useState<any[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [hourly, setHourly] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/analytics/revenue'),
      api.get('/analytics/carriers'),
      api.get('/analytics/hourly'),
    ]).then(([r, c, h]) => {
      setRevenue(r.data.map((d: any) => ({ ...d, day: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })));
      setCarriers(c.data.slice(0, 10));
      setHourly(h.data.map((d: any) => ({ ...d, hour: d.hour + ':00' })));
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant='h5' fontWeight={700} mb={3}>Analytics</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label='Revenue' />
        <Tab label='Carriers' />
        <Tab label='Traffic Pattern' />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant='h6' fontWeight={600} mb={2}>Revenue & Cost - Last 30 Days</Typography>
            <ResponsiveContainer width='100%' height={350}>
              <AreaChart data={revenue}>
                <defs>
                  <linearGradient id='rev' x1='0' y1='0' x2='0' y2='1'><stop offset='5%' stopColor='#1976d2' stopOpacity={0.3}/><stop offset='95%' stopColor='#1976d2' stopOpacity={0}/></linearGradient>
                  <linearGradient id='margin' x1='0' y1='0' x2='0' y2='1'><stop offset='5%' stopColor='#2e7d32' stopOpacity={0.3}/><stop offset='95%' stopColor='#2e7d32' stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='day' />
                <YAxis tickFormatter={(v) => '$' + parseFloat(v).toFixed(2)} />
                <Tooltip formatter={(v: any) => '$' + parseFloat(v).toFixed(4)} />
                <Legend />
                <Area type='monotone' dataKey='revenue' stroke='#1976d2' fill='url(#rev)' name='Revenue' />
                <Area type='monotone' dataKey='margin' stroke='#2e7d32' fill='url(#margin)' name='Margin' />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant='h6' fontWeight={600} mb={2}>Top Carriers by Volume</Typography>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={carriers} layout='vertical'>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis type='number' />
                    <YAxis type='category' dataKey='carrier_name' width={100} />
                    <Tooltip />
                    <Bar dataKey='calls' fill='#1976d2' name='Calls' radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant='h6' fontWeight={600} mb={2}>ASR by Carrier</Typography>
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie data={carriers.slice(0,6)} dataKey='asr' nameKey='carrier_name' cx='50%' cy='50%' outerRadius={100} label={({ name, value }) => name?.substring(0,8) + ': ' + value + '%'}>
                      {carriers.slice(0,6).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: any) => v + '%'} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography variant='h6' fontWeight={600} mb={2}>Hourly Traffic Pattern</Typography>
            <ResponsiveContainer width='100%' height={350}>
              <BarChart data={hourly}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='hour' />
                <YAxis />
                <Tooltip />
                <Bar dataKey='calls' fill='#1976d2' name='Calls' radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
